// @flow

import type { Action } from "redux-action";
import { beginRequest, completeRequest } from "@store/utils";
import type { ApprovableContainer } from "@modules/service/types";
import type { EapState, EapRevision } from "./types";

export type ShowAddModalRequest = $Exact<{
  show: boolean,
  step?: number,
}>;

const startRequest = beginRequest(["requestsInProgress"]);

const finishRequest = completeRequest(["requestsInProgress"]);

// Get Eap List

export const onGetEapList = (state: EapState) => () => startRequest(state);

export const onGetEapListSuccess = (state: EapState) => ({ payload }: Object) => ({
  ...state,
  ...finishRequest(state),
  error: null,
  list: payload,
});

export const onGetEapListError = (state: EapState) => ({ payload }: Object) => ({
  ...state,
  ...finishRequest(state),
  success: false,
  error: payload,
});

// Get Eap Record

export const onGetEap = (state: EapState) => () => startRequest(state);

export const onGetEapSuccess = (state: EapState) => ({ payload }: Object) => ({
  ...state,
  ...finishRequest(state),
  error: null,
  current: payload,
});

export const onGetEapError = (state: EapState) => ({ payload }: Object) => ({
  ...state,
  ...finishRequest(state),
  success: false,
  error: payload,
});

// Get Eap Schema

export const onGetEapSchema = (state: EapState) => () => startRequest(state);

export const onGetEapSchemaSuccess = (state: EapState) => ({ payload }: Object) => ({
  ...state,
  ...finishRequest(state),
  error: null,
  schema: payload,
});

export const onGetEapSchemaError = (state: EapState) => ({ payload }: Object) => ({
  ...state,
  ...finishRequest(state),
  success: false,
  error: payload,
});

// Get Eap Absence Reasons

export const onGetEapAbsenceReasons = (state: EapState) => () => startRequest(state);

export const onGetEapAbsenceReasonsSuccess = (state: EapState) => ({ payload }: Object) => ({
  ...state,
  ...finishRequest(state),
  error: null,
  options: {
    absenceReasons: payload,
  },
});

export const onGetEapAbsenceReasonsError = (state: EapState) => ({ payload }: Object) => ({
  ...state,
  ...finishRequest(state),
  success: false,
  error: payload,
});

// Create

export const onNewEap = (state: EapState) => (action: Action<void>) => ({
  ...state,
  draft: {
    name: null,
    phoneNumber: null,
    usageInstructions: null,
    reasonSpecificUsages: [],
  },
});

export const onStartDraftEapSuccess = (state: EapState) => (
  action: Action<ApprovableContainer<EapRevision>>,
) => ({
  ...state,
  draft: (action.payload && action.payload.active) || {},
});

export const onDuplicateEapSuccess = (state: EapState) => (action: Action<EapRevision>) => ({
  ...state,
  draft: {
    name: null,
    phoneNumber: (action.payload || {}).phoneNumber,
    usageInstructions: (action.payload || {}).usageInstructions,
    reasonSpecificUsages: (action.payload || {}).reasonSpecificUsages,
  },
});

export const setEapDraft = (state: EapState) => ({ payload }: Object) => ({
  ...state,
  draft: payload,
});

export const onNewEapAssignments = (state: EapState) => (action: Action<void>) => ({
  ...state,
  assignments: [],
});

export const setEapAssignments = (state: EapState) => ({ payload }: Object) => ({
  ...state,
  assignments: payload,
});

export const onSetEapDraftAssignments = (state: EapState) => ({ payload }: Object) => ({
  ...state,
  draftAssignments: payload,
});

// Config

export const onSetEapEnabled = (state: EapState) => ({ payload }: Object) => ({
  ...state,
  configuration: {
    ...state.configuration,
    enabled: payload,
  },
});

// Modals

export const onShowAddModal = (state: EapState) => ({ payload }: Action<ShowAddModalRequest>) => {
  if (!payload) {
    return state;
  }

  return {
    ...state,
    modals: {
      ...state.modals,
      showAddModal: payload.show,
      modalStep: payload.step || state.modals.modalStep,
    },
  };
};

export const onShowEditModal = (state: EapState) => ({ payload }: Action<ShowAddModalRequest>) => {
  if (!payload) {
    return state;
  }

  return {
    ...state,
    modals: {
      ...state.modals,
      showEditModal: payload.show,
      modalStep: payload.step || state.modals.modalStep,
    },
  };
};

export const onShowEditAssignmentsModal = (state: EapState) => ({ payload }: Object) => ({
  ...state,
  modals: {
    ...state.modals,
    showEditAssignmentsModal: payload,
  },
});

export const onSetEapShowDiscardModal = (state: EapState) => ({ payload }: Object) => ({
  ...state,
  modals: {
    ...state.modals,
    showDiscardModal: payload,
  },
});

export const onSetEapShowSubmitModal = (state: EapState) => ({ payload }: Object) => ({
  ...state,
  modals: {
    ...state.modals,
    showSubmitModal: payload,
  },
});

export const onSetEapShowCancelModal = (state: EapState) => ({ payload }: Object) => ({
  ...state,
  modals: {
    ...state.modals,
    showCancelModal: payload,
  },
});

export const onSetEapShowCancelDiscardModal = (state: EapState) => ({ payload }: Object) => ({
  ...state,
  modals: {
    ...state.modals,
    showCancelDiscardModal: payload,
  },
});

export const onSetEapShowDisableModal = (state: EapState) => ({ payload }: Object) => ({
  ...state,
  modals: {
    ...state.modals,
    showDisableModal: payload,
  },
});
